import { apiPath, requestJson } from "./client.js";
import type {
  ConsoleSummary,
  CountDefinitionCatalog,
  CoverageReport,
  LicenceBanner,
  LicenceStatus,
  PermissionAction,
  ReportCatalog,
  ReportingOverview,
  SessionContext,
  TenantRole,
} from "./types.js";

interface SessionWire {
  operator?: unknown;
  principalId?: unknown;
  tenantId?: unknown;
  tenantRole?: unknown;
  role?: unknown;
  permissionPolicyVersion?: unknown;
  scopeRevision?: unknown;
  effectiveScopes?: unknown;
  allowedActions?: unknown;
  expiresAt?: unknown;
  entitlement?: unknown;
  csrf?: unknown;
}

const ROLES: readonly TenantRole[] = ["owner", "risk_manager", "operator", "reviewer", "viewer"];

const ROLE_PRESENTATION_ACTIONS: Record<TenantRole, readonly PermissionAction[]> = {
  viewer: [
    "overview:read",
    "findings:read",
    "scans:read",
    "sources:read",
    "help:read",
    "reports:read",
  ],
  reviewer: [
    "overview:read",
    "findings:read",
    "findings:review",
    "scans:read",
    "sources:read",
    "remediation:read",
    "audit:read",
    "help:read",
    "reports:read",
    "reports:download",
  ],
  risk_manager: [
    "overview:read",
    "findings:read",
    "findings:review",
    "findings:approve",
    "scans:read",
    "sources:read",
    "remediation:read",
    "remediation:approve",
    "audit:read",
    "help:read",
    "reports:read",
    "reports:download",
  ],
  operator: [
    "overview:read",
    "findings:read",
    "findings:review",
    "scans:read",
    "scans:write",
    "sources:read",
    "sources:write",
    "delivery:read",
    "remediation:read",
    "remediation:write",
    "audit:read",
    "help:read",
    "reports:read",
    "reports:download",
  ],
  owner: [
    "overview:read",
    "findings:read",
    "findings:review",
    "findings:approve",
    "scans:read",
    "scans:write",
    "sources:read",
    "sources:write",
    "delivery:read",
    "remediation:read",
    "remediation:write",
    "remediation:approve",
    "audit:read",
    "providers:read",
    "providers:write",
    "admin:read",
    "admin:write",
    "help:read",
    "reports:read",
    "reports:download",
  ],
};

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

const LICENCE_STATES: readonly LicenceStatus["state"][] = [
  "valid",
  "grace",
  "degraded",
  "expired",
  "none",
  "unprovisioned",
];

/** Malformed or absent licence data yields null (no banner); it can never grant anything. */
export function normalizeLicence(value: unknown): LicenceStatus | null {
  if (value === null || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const state = LICENCE_STATES.find((candidate) => candidate === raw.state);
  if (!state) return null;
  const banner = raw.banner as Record<string, unknown> | null | undefined;
  const validBanner: LicenceBanner | null =
    banner &&
    (banner.level === "amber" || banner.level === "red") &&
    typeof banner.code === "string" &&
    typeof banner.message === "string"
      ? { level: banner.level, code: banner.code, message: banner.message }
      : null;
  return {
    state,
    canStartScans: raw.canStartScans === true,
    canAddSources: raw.canAddSources === true,
    banner: validBanner,
  };
}

export function normalizeSession(wire: SessionWire): SessionContext {
  const principalId = typeof wire.principalId === "string" ? wire.principalId : wire.operator;
  const roleValue = typeof wire.role === "string" ? wire.role : wire.tenantRole;
  if (
    typeof principalId !== "string" ||
    typeof wire.tenantId !== "string" ||
    typeof roleValue !== "string" ||
    !ROLES.includes(roleValue as TenantRole)
  ) {
    throw new Error("The session response is missing its tenant-scoped identity.");
  }
  const role = roleValue as TenantRole;
  const serverActions = strings(wire.allowedActions) as PermissionAction[];
  const hasServerActions = Array.isArray(wire.allowedActions);
  return {
    principalId,
    tenantId: wire.tenantId,
    role,
    permissionPolicyVersion:
      typeof wire.permissionPolicyVersion === "string" ? wire.permissionPolicyVersion : null,
    scopeRevision: typeof wire.scopeRevision === "string" ? wire.scopeRevision : null,
    effectiveScopes: strings(wire.effectiveScopes),
    // Compatibility for the current session endpoint. This list only controls
    // presentation and cannot grant access: APIs still evaluate every request.
    allowedActions: hasServerActions ? serverActions : ROLE_PRESENTATION_ACTIONS[role],
    expiresAt: typeof wire.expiresAt === "string" ? wire.expiresAt : null,
    licence: normalizeLicence(wire.entitlement),
  };
}

export function sessionAllows(session: SessionContext | null, action: PermissionAction): boolean {
  return session?.allowedActions.includes(action) ?? false;
}

export const commandCenterApi = {
  async getSession(signal?: AbortSignal): Promise<SessionContext> {
    const wire = await requestJson<SessionWire>(apiPath("/auth/session"), signal ? { signal } : {});
    return normalizeSession(wire);
  },

  async signOut(signal?: AbortSignal): Promise<void> {
    const wire = await requestJson<SessionWire>(apiPath("/auth/session"), signal ? { signal } : {});
    if (typeof wire.csrf !== "string") throw new Error("The session cannot be signed out safely.");
    const response = await fetch(apiPath("/auth/session"), {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "X-Assure-CSRF": wire.csrf },
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) throw new Error("Sign out could not be completed.");
  },

  getOverview(scanId?: string, signal?: AbortSignal): Promise<ReportingOverview> {
    const params = new URLSearchParams();
    if (scanId) params.set("scanId", scanId);
    return requestJson(apiPath("/reporting/overview", params), signal ? { signal } : {});
  },

  getInventoryOverview(scanId?: string, signal?: AbortSignal): Promise<ReportingOverview> {
    const params = new URLSearchParams({ inventoryRows: "1" });
    if (scanId) params.set("scanId", scanId);
    return requestJson(apiPath("/reporting/overview", params), signal ? { signal } : {});
  },

  sbomDownloadHref(scanId?: string): string {
    const params = new URLSearchParams();
    if (scanId) params.set("scanId", scanId);
    return apiPath("/reporting/inventory/sbom.cdx.json", params);
  },

  getStatus(scanId?: string, signal?: AbortSignal): Promise<ConsoleSummary> {
    const params = new URLSearchParams();
    if (scanId) params.set("scanId", scanId);
    return requestJson(apiPath("/reporting/status", params), signal ? { signal } : {});
  },

  getCoverage(
    scanId?: string,
    matrixPage = 1,
    signal?: AbortSignal,
    heatmapPage = 1,
  ): Promise<CoverageReport> {
    const params = new URLSearchParams();
    if (scanId) params.set("scanId", scanId);
    params.set("matrixPage", String(matrixPage));
    params.set("matrixPageSize", "100");
    params.set("heatmapPage", String(heatmapPage));
    params.set("heatmapPageSize", "50");
    return requestJson(apiPath("/reporting/coverage", params), signal ? { signal } : {});
  },

  getReports(signal?: AbortSignal): Promise<ReportCatalog> {
    return requestJson(apiPath("/reporting/reports"), signal ? { signal } : {});
  },

  getCountDefinitions(signal?: AbortSignal): Promise<CountDefinitionCatalog> {
    return requestJson(apiPath("/reporting/count-definitions"), signal ? { signal } : {});
  },
};
